import React, { createContext, useContext, useEffect, useState } from 'react';

export type SiteLang = 'ro' | 'en';

const dict = {
  ro: {
    nav: {
      home: 'Acasă',
      platforma: 'Platformă',
      solutii: 'Soluții',
      tehnologie: 'Tehnologie',
      industrii: 'Industrii',
      despre: 'Despre',
      contact: 'Contact',
      login: 'Înregistrare',
      demo: 'Cere demo',
    },
    hero: {
      badge: 'AETHERIS · Operating System pentru orașe, clădiri și sănătate',
      title1: 'O singură platformă.',
      title2: 'Infinite posibilități.',
      subtitle:
        'Misiunea noastră este să oferim soluții concrete, transformând viziunea dumneavoastră într-o realitate de succes.',
      ctaPrimary: 'Cere demo gratuit',
      ctaSecondary: 'Explorează platforma',
      stats: [
        { v: '117', l: 'module integrate' },
        { v: '99.98%', l: 'uptime garantat' },
        { v: '< 200ms', l: 'latență API' },
        { v: 'EU', l: 'data residency' },
      ],
    },
    pillars: {
      label: 'Cei 8 piloni',
      title: 'O platformă. Opt verticale operaționale.',
      subtitle:
        'Fiecare modul funcționează autonom și se integrează nativ cu celelalte — o singură bază de date, o singură identitate, o singură politică de securitate.',
      items: [
        {
          tag: 'Smart City',
          title: 'AETHERIS City OS',
          desc: 'Trafic predictiv, mediu, parcări inteligente, iluminat public, cetățeni — un creier urban unificat.',
        },
        {
          tag: 'Smart Building',
          title: 'AETHERIS Building & KNX',
          desc: 'BMS cloud-native, KNX/IP integrat, energie, securitate, confort — pentru dezvoltatori și administratori.',
        },
        {
          tag: 'Telemedicină',
          title: 'AETHERIS Care',
          desc: 'Medic AI, Pacient X, consultații video criptate end-to-end și documente clinice oficiale.',
        },
        {
          tag: 'Sănătate Mintală',
          title: 'AETHERIS Mind',
          desc: 'Recuperare digitală pentru adicții (joc patologic, substanțe), CBT, cue-exposure și telemedicină 1:1.',
        },
        {
          tag: 'Cybersecurity',
          title: 'AETHERIS Secure',
          desc: 'Dashboard NIS2 live, runtime pentest, GDPR & DSA compliance, MFA, audit complet.',
        },
        {
          tag: 'AI Gateway',
          title: 'AETHERIS Intelligence',
          desc: 'AI conversațional, vocal și vizual cu agenți specializați — integrat nativ în fiecare modul, fără cod.',
        },
        {
          tag: 'Digital Twin',
          title: 'AETHERIS Digital Twin',
          desc: 'Geamăn digital operațional pentru spital, oraș și clădire: model 3D/BIM sincronizat, simulare what-if și mentenanță predictivă.',
        },
        {
          tag: 'ERP & Finance',
          title: 'AETHERIS Finance',
          desc: 'Facturare, încasări, plăți, gestiuni, KPI-uri — automatizate cu parsere AI și rapoarte PDF oficiale.',
        },
      ],
      cta: 'Vezi toate soluțiile',
    },
    tech: {
      label: 'Tehnologie',
      title: 'Construit pentru scală europeană',
      items: [
        { t: 'AI-native', d: 'AI conversațional, vocal și vizual cu agenți specializați pe orice flux operațional.' },
        { t: 'Realtime', d: 'WebRTC pentru consultații, KNX/IP live, dashboard-uri actualizate în milisecunde.' },
        { t: 'Securitate clinică', d: 'RLS pe toate tabelele, MFA, sesiuni 60m, audit log, criptare end-to-end.' },
        { t: 'Conform UE', d: 'GDPR, NIS2, DSA, data residency în EU, DPIA și DSA flag integrate.' },
        { t: 'Open & integrabil', d: 'API REST, webhooks, SSO Google/Microsoft, ANAF, CNAS, Resend, Stripe.' },
        { t: 'PWA & mobil', d: 'Instalabil pe orice device, push notifications RFC 8291, offline-first selectiv.' },
      ],
    },
    industries: {
      label: 'Industrii',
      title: 'Construită pentru cei care decid',
      items: [
        { t: 'Administrație publică', d: 'Primării, ministere, regii autonome — Smart City complet operațional.' },
        { t: 'Sănătate', d: 'Spitale, clinici private, cabinete — telemedicină și AI clinic certificat.' },
        { t: 'Imobiliare', d: 'Dezvoltatori, asociații de proprietari, retail — BMS și management integrat.' },
        { t: 'Hospitality', d: 'Hoteluri, restaurante, centre comerciale — operațiuni unificate într-un dashboard.' },
        { t: 'Educație', d: 'Universități, școli — clădiri smart, securitate, comunitate.' },
        { t: 'Industrie', d: 'Fabrici, depozite — KNX/SCADA, IoT, mentenanță predictivă AI.' },
      ],
    },
    proof: {
      title: 'Aleasă de organizații serioase',
      subtitle:
        'De la primării la spitale și dezvoltatori imobiliari, AETHERIS rulează deja în producție pe trei continente.',
    },
    pricing: {
      label: 'Preț',
      title: 'Investiție previzibilă, valoare imediată',
      subtitle:
        'Pricing transparent, scalabil per modul și per utilizator. Demo, audit și ofertă personalizată — gratuite.',
      cta: 'Cere ofertă personalizată',
    },
    finalCta: {
      title: 'Gata să construim viitorul împreună?',
      subtitle:
        'Vorbește cu echipa AETHERIS. Răspundem în 24h cu o propunere personalizată pentru organizația ta.',
      btn: 'Programează un demo',
    },
    footer: {
      tagline: 'Operating System-ul pentru orașe, clădiri și sănătate.',
      product: 'Produs',
      company: 'Companie',
      legal: 'Legal',
      contact: 'Contact',
      rights: 'Toate drepturile rezervate.',
      links: {
        platforma: 'Platformă',
        solutii: 'Soluții',
        tehnologie: 'Tehnologie',
        despre: 'Despre noi',
        cariere: 'Cariere',
        blog: 'Blog',
        gdpr: 'GDPR',
        termeni: 'Termeni',
        cookies: 'Cookies',
      },
    },
    contact: {
      title: 'Hai să vorbim',
      subtitle: 'Spune-ne ce vrei să construiești. Răspundem rapid și serios.',
      name: 'Nume complet',
      email: 'Email profesional',
      org: 'Organizație',
      role: 'Sunt...',
      roles: ['Persoană fizică', 'Companie privată', 'Administrație publică', 'Spital / clinică', 'Altele'],
      message: 'Mesaj',
      send: 'Trimite mesajul',
      sending: 'Se trimite...',
      success: 'Mesaj trimis. Revenim în 24h.',
      error: 'Nu am putut trimite. Încearcă din nou.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      platforma: 'Platform',
      solutii: 'Solutions',
      tehnologie: 'Technology',
      industrii: 'Industries',
      despre: 'About',
      contact: 'Contact',
      login: 'Sign up',
      demo: 'Request demo',
    },
    hero: {
      badge: 'AETHERIS · Operating System for cities, buildings and healthcare',
      title1: 'One platform.',
      title2: 'Infinite possibilities.',
      subtitle:
        'Our mission is to deliver concrete solutions, transforming your vision into a successful reality.',
      ctaPrimary: 'Get a free demo',
      ctaSecondary: 'Explore the platform',
      stats: [
        { v: '117', l: 'integrated modules' },
        { v: '99.98%', l: 'guaranteed uptime' },
        { v: '< 200ms', l: 'API latency' },
        { v: 'EU', l: 'data residency' },
      ],
    },
    pillars: {
      label: 'The 8 pillars',
      title: 'One platform. Eight operational verticals.',
      subtitle:
        'Every module runs autonomously and integrates natively with the rest — one database, one identity, one security policy.',
      items: [
        { tag: 'Smart City', title: 'AETHERIS City OS', desc: 'Predictive traffic, environment, smart parking, public lighting, citizens — one urban brain.' },
        { tag: 'Smart Building', title: 'AETHERIS Building & KNX', desc: 'Cloud-native BMS, KNX/IP integrated, energy, security, comfort — for developers and operators.' },
        { tag: 'Telemedicine', title: 'AETHERIS Care', desc: 'Medic AI, Patient X, end-to-end encrypted video and official clinical documents.' },
        { tag: 'Mental Health', title: 'AETHERIS Mind', desc: 'Digital recovery for addictions (gambling, substances), CBT, cue-exposure and 1:1 telemedicine.' },
        { tag: 'Cybersecurity', title: 'AETHERIS Secure', desc: 'Live NIS2 dashboard, runtime pentest, GDPR & DSA compliance, MFA, full audit trail.' },
        { tag: 'AI Gateway', title: 'AETHERIS Intelligence', desc: 'Conversational, voice and vision AI with specialised agents — natively embedded in every module, no code.' },
        { tag: 'Digital Twin', title: 'AETHERIS Digital Twin', desc: 'Operational digital twin for hospital, city and building: synced 3D/BIM model, what-if simulation and predictive maintenance.' },
        { tag: 'ERP & Finance', title: 'AETHERIS Finance', desc: 'Invoicing, collections, payments, inventory, KPIs — automated with AI parsers and PDF reports.' },
      ],
      cta: 'See all solutions',
    },
    tech: {
      label: 'Technology',
      title: 'Engineered for European scale',
      items: [
        { t: 'AI-native', d: 'Conversational, voice and vision AI with specialised agents on every operational flow.' },
        { t: 'Realtime', d: 'WebRTC for consultations, live KNX/IP, dashboards updated in milliseconds.' },
        { t: 'Clinical-grade security', d: 'RLS on every table, MFA, 60m sessions, audit log, end-to-end encryption.' },
        { t: 'EU compliant', d: 'GDPR, NIS2, DSA, EU data residency, built-in DPIA and DSA flag.' },
        { t: 'Open & integrable', d: 'REST API, webhooks, Google/Microsoft SSO, ANAF, CNAS, Resend, Stripe.' },
        { t: 'PWA & mobile', d: 'Installable on any device, RFC 8291 push, selective offline-first.' },
      ],
    },
    industries: {
      label: 'Industries',
      title: 'Built for decision makers',
      items: [
        { t: 'Public administration', d: 'City halls, ministries, public utilities — full Smart City in production.' },
        { t: 'Healthcare', d: 'Hospitals, private clinics, practices — telemedicine and certified clinical AI.' },
        { t: 'Real estate', d: 'Developers, owner associations, retail — integrated BMS and management.' },
        { t: 'Hospitality', d: 'Hotels, restaurants, malls — unified operations in one dashboard.' },
        { t: 'Education', d: 'Universities, schools — smart buildings, security, community.' },
        { t: 'Industry', d: 'Factories, warehouses — KNX/SCADA, IoT, predictive AI maintenance.' },
      ],
    },
    proof: {
      title: 'Trusted by serious organisations',
      subtitle: 'From city halls to hospitals and real-estate developers, AETHERIS already runs in production on three continents.',
    },
    pricing: {
      label: 'Pricing',
      title: 'Predictable investment, immediate value',
      subtitle: 'Transparent pricing, scalable per module and per user. Demo, audit and tailored quote — free.',
      cta: 'Get a tailored quote',
    },
    finalCta: {
      title: 'Ready to build the future together?',
      subtitle: 'Talk to the AETHERIS team. We respond in 24h with a tailored proposal for your organisation.',
      btn: 'Book a demo',
    },
    footer: {
      tagline: 'The Operating System for cities, buildings and healthcare.',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      contact: 'Contact',
      rights: 'All rights reserved.',
      links: {
        platforma: 'Platform',
        solutii: 'Solutions',
        tehnologie: 'Technology',
        despre: 'About us',
        cariere: 'Careers',
        blog: 'Blog',
        gdpr: 'GDPR',
        termeni: 'Terms',
        cookies: 'Cookies',
      },
    },
    contact: {
      title: "Let's talk",
      subtitle: 'Tell us what you want to build. We respond quickly and professionally.',
      name: 'Full name',
      email: 'Work email',
      org: 'Organisation',
      role: 'I am...',
      roles: ['Individual', 'Private company', 'Public administration', 'Hospital / clinic', 'Other'],
      message: 'Message',
      send: 'Send message',
      sending: 'Sending...',
      success: "Message sent. We'll get back to you within 24 hours.",
      error: 'Could not send. Please try again.',
    },
  },
} as const;

type Dict = (typeof dict)['ro'];

interface Ctx {
  lang: SiteLang;
  setLang: (l: SiteLang) => void;
  t: Dict;
}

const SiteI18nContext = createContext<Ctx | undefined>(undefined);

export const SiteI18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<SiteLang>(() => {
    const saved = (typeof window !== 'undefined' && localStorage.getItem('site-lang')) as SiteLang | null;
    if (saved === 'ro' || saved === 'en') return saved;
    if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en')) return 'en';
    return 'ro';
  });

  useEffect(() => {
    localStorage.setItem('site-lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <SiteI18nContext.Provider value={{ lang, setLang, t: dict[lang] as Dict }}>
      {children}
    </SiteI18nContext.Provider>
  );
};

export function useSiteI18n() {
  const c = useContext(SiteI18nContext);
  if (!c) throw new Error('useSiteI18n must be used inside SiteI18nProvider');
  return c;
}
